import { Grid, Spinner, Text } from "@radix-ui/themes";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";

type MemoriesInfiniteScrollTriggerProps = {
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore(): void;
};

/**
 * Memories-specific infinite-scroll sentinel. Deliberately NOT the shared
 * `InfiniteScrollTrigger` — that one is coupled to the Redux knowledge-database
 * slice and the model-records endpoint. This one drives a plain react-query
 * load-more callback, no Redux.
 */
export function MemoriesInfiniteScrollTrigger({
  hasMore,
  isLoading,
  onLoadMore,
}: MemoriesInfiniteScrollTriggerProps) {
  const { t } = useTranslation("ui");
  const { ref, inView } = useInView({ threshold: 0 });

  useEffect(() => {
    if (inView && hasMore && !isLoading) {
      onLoadMore();
    }
  }, [inView, hasMore, isLoading, onLoadMore]);

  if (!hasMore) return null;

  return (
    <Grid
      ref={ref}
      py="4"
      gap="3"
      maxWidth="max-content"
      mx="auto"
      data-testid="memories-infinite-scroll"
    >
      <Spinner mx="auto" />
      <Text>{t("settings.memoriesInfiniteScrollTrigger.loadingMore")}</Text>
    </Grid>
  );
}
